<script setup>
import { ElMessageBox } from 'element-plus';
import { Mail } from 'lucide-vue-next';

import { useReceivedInvitesStore } from '@/stores/receivedInvites';

const props = defineProps({
  invite: {
    type: Object,
    required: true,
  },
});

const store = useReceivedInvitesStore();

const onAccept = async () => {
  try {
    // Name the specific business profile when the backend supplies it (WP-955); fall back to the
    // generic phrasing for older invites that predate the business_profile_name field.
    const account = props.invite.businessProfileName
      ? `${props.invite.businessProfileName} on WisePPC`
      : 'their WisePPC account';
    // Match the figma "Are You Sure?" confirmation before joining another account.
    await ElMessageBox.confirm(
      `You're about to accept ${props.invite.inviterName}'s invitation to join ${account}. By confirming, you'll gain access to their business account with the permissions they've assigned to you.`,
      'Are you sure?',
      {
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        type: 'success',
      },
    );
  } catch {
    return; // dismissed / cancelled
  }
  await store.accept(props.invite);
};

const onReject = () => store.reject(props.invite);
</script>

<template>
  <div class="invite-bar">
    <span class="invite-bar__message">
      <Mail
        :size="16"
        class="invite-bar__icon"
      />
      <span v-if="invite.businessProfileName">
        <strong v-no-translate>{{ invite.inviterName }}</strong>
        {{ $t('has invited you to join') }}
        <strong v-no-translate>{{ invite.businessProfileName }}</strong>
        {{ $t('on WisePPC') }}
      </span>
      <span v-else>
        <strong v-no-translate>{{ invite.inviterName }}</strong>
        {{ $t('has invited you to join their WisePPC account') }}
      </span>
    </span>
    <span class="invite-bar__actions">
      <el-button
        size="small"
        :loading="store.isProcessing(invite.code)"
        @click="onReject"
      >
        {{ $t('Reject') }}
      </el-button>
      <el-button
        type="primary"
        size="small"
        :loading="store.isProcessing(invite.code)"
        @click="onAccept"
      >
        {{ $t('Accept') }}
      </el-button>
    </span>
  </div>
</template>

<style lang="scss" scoped>
.invite-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  padding: 8px 16px;
  background-color: var(--wp-surface-secondary);
  // The header below shares the same lavender in light theme — a brand accent divider keeps
  // the bar visually distinct (a plain gray border is too subtle against the lavender).
  border-bottom: 1px solid var(--wp-brand-40);
  color: var(--wp-text-primary);
  flex-wrap: wrap;

  &__message {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  &__icon {
    color: var(--wp-text-secondary);
    flex-shrink: 0;
  }

  &__actions {
    display: inline-flex;
    align-items: center;
  }
}
</style>
